import { getLogger } from "@momentum/logger";
import { momentumApi } from "./momentumApi";
import {
  type LeagueId,
  anyScoresFeedSchema,
  anyGameStateSchema,
  anyGamePbpSchema,
  liveGameScoreNewTimeSeriesPointSchema,
  liveGameScoreboardUpdateSchema,
  livePbpEventUpdateSchema,
  livePbpEventDeleteSchema,
  liveGameTeamStatsUpdateSchema,
} from "@momentum/schemas";
import {
  createAsyncThunk,
  createListenerMiddleware,
  isRejected,
  type Action,
  type AsyncThunk,
  type ThunkAction,
} from "@reduxjs/toolkit";
import {
  dynamicMiddleware,
  type LazyLoadedSlices,
} from "@momentum/redux-store";
import {
  messageReceived,
  realtimeApi,
  type RealtimeMessage,
} from "@momentum/realtime-api";
import type z from "zod/v4";

const logger = getLogger().child().withMetadata({
  source: "liveApi",
});

export const liveApi = momentumApi.injectEndpoints({
  endpoints: (builder) => ({
    scoresFeed: builder.query({
      query: ({ leagueId }: { leagueId: LeagueId }) =>
        `${leagueId}/live-scores`,
      responseSchema: anyScoresFeedSchema,
      skipSchemaValidation: !import.meta.env.VITE_MOMENTUM_VALIDATE_SCHEMAS,
      providesTags(result, error, arg, meta) {
        return [{ type: "ScoresFeed" as const, leagueId: arg.leagueId }];
      },
      async onCacheEntryAdded(_arg, api) {
        const { data } = await api.cacheDataLoaded;
        const { channelInfo } = data;

        const { unsubscribe } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: channelInfo.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        await api.cacheEntryRemoved;

        unsubscribe();
      },
    }),
    game: builder.query({
      query: ({ eventId, leagueId }: { eventId: string; leagueId: LeagueId }) =>
        `${leagueId}/games/${eventId}`,
      responseSchema: anyGameStateSchema,
      skipSchemaValidation: !import.meta.env.VITE_MOMENTUM_VALIDATE_SCHEMAS,
      providesTags(result, error, arg, meta) {
        return [
          {
            type: "GameState" as const,
            eventId: arg.eventId,
            leagueId: arg.leagueId,
          },
        ];
      },
      async onCacheEntryAdded(args, api) {
        const { data } = await api.cacheDataLoaded;
        const {
          channels: {
            scoreboard,
            teamStats,
            leagueScores,
            pbpEvents,
            gameControl,
          },
        } = data;

        const { unsubscribe: unsubscribeScoreboard } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: scoreboard.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        const { unsubscribe: unsubscribeTeamStats } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: teamStats.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        const { unsubscribe: unsubscribeLeagueScores } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: leagueScores.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        const { unsubscribe: unsubscribePbpEvents } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: pbpEvents.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        const { unsubscribe: unsubscribeGameControl } = api.dispatch(
          realtimeApi.endpoints.subscribeChannel.initiate({
            channel: gameControl.channel,
            params: {
              rewind: "1",
              delta: "vcdiff",
            },
          })
        );

        await api.cacheEntryRemoved;

        unsubscribeScoreboard();
        unsubscribeTeamStats();
        unsubscribeLeagueScores();
        unsubscribePbpEvents();
        unsubscribeGameControl();
      },
    }),
    gamePbp: builder.query({
      query: ({
        eventId,
        leagueId,
        lastN,
      }: {
        eventId: string;
        leagueId: LeagueId;
        lastN?: number;
      }) => ({
        url: `${leagueId}/games/${eventId}/pbp`,
      }),
      responseSchema: anyGamePbpSchema,
      skipSchemaValidation: !import.meta.env.VITE_MOMENTUM_VALIDATE_SCHEMAS,
      providesTags(result, error, arg, meta) {
        return [
          {
            type: "GameState" as const,
            eventId: arg.eventId,
            leagueId: arg.leagueId,
          },
          {
            type: "Pbp" as const,
          },
        ];
      },
    }),
  }),
});

function parse<T>(schema: z.ZodSchema<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (result.error) {
    logger.error(result.error.message);
    console.error(result.error);
    return data as T;
  } else {
    return result.data;
  }
}

const messageHandlers = {
  liveGameScoreNewTimeSeriesPoint: createAsyncThunk(
    "momentumApi/realtime/liveGameScoreNewTimeSeriesPoint",
    async (message: RealtimeMessage, api) => {
      const { data } = parse(liveGameScoreNewTimeSeriesPointSchema, message);

      const scoresFeed = liveApi.endpoints.scoresFeed.select({
        leagueId: data.leagueId,
      })(api.getState() as LazyLoadedSlices);

      if (scoresFeed.data) {
        const existingGame = scoresFeed.data.games.find(
          (game) => game.eventId === data.eventId
        );

        if (existingGame) {
          api.dispatch(
            liveApi.util.updateQueryData(
              "scoresFeed",
              { leagueId: data.leagueId },
              (draft) => {
                const game = draft.games.find(
                  (game) => game.eventId === data.eventId
                );
                if (!game) {
                  return;
                }

                const index = game.sparkline.findIndex(
                  (point) => point.gameSecs === data.point.gameSecs
                );
                if (index === -1) {
                  game.sparkline.push(data.point);
                  game.sparkline.sort((a, b) => a.gameSecs - b.gameSecs);
                } else {
                  game.sparkline[index] = data.point;
                }

                game.clock.s = data.point.clockSecs;
                game.clock.fmt = data.point.clockFmt;

                game.away.points = data.point.aPts;
                game.home.points = data.point.hPts;

                game.period = data.point.period;
              }
            ) as ThunkAction<void, unknown, unknown, Action>
          );
        } else {
          api.dispatch(liveApi.util.invalidateTags(["ScoresFeed"]));
        }
      }

      const gameState = liveApi.endpoints.game.select({
        eventId: data.eventId,
        leagueId: data.leagueId,
      })(api.getState() as LazyLoadedSlices);

      if (gameState.data) {
        api.dispatch(
          liveApi.util.updateQueryData(
            "game",
            { eventId: data.eventId, leagueId: data.leagueId },
            (draft) => {
              if (draft.sparkline) {
                const index = draft.sparkline.findIndex(
                  (point) => point.gameSecs === data.point.gameSecs
                );
                if (index === -1) {
                  draft.sparkline.push(data.point);
                  draft.sparkline.sort((a, b) => a.gameSecs - b.gameSecs);
                } else {
                  draft.sparkline[index] = data.point;
                }
              } else {
                draft.sparkline = [data.point];
              }
            }
          ) as ThunkAction<void, unknown, unknown, Action>
        );
      }
    }
  ),
  liveGameScoreboardUpdate: createAsyncThunk(
    "momentumApi/realtime/liveGameScoreboardUpdate",
    async (message: RealtimeMessage, api) => {
      const { data } = parse(liveGameScoreboardUpdateSchema, message);

      const scoresFeed = liveApi.endpoints.scoresFeed.select({
        leagueId: data.leagueId,
      })(api.getState() as LazyLoadedSlices);

      if (scoresFeed.data) {
        const existingGame = scoresFeed.data.games.find(
          (game) => game.eventId === data.eventId
        );
        if (existingGame) {
          api.dispatch(
            liveApi.util.updateQueryData(
              "scoresFeed",
              { leagueId: data.leagueId },
              (draft) => {
                const game = draft.games.find(
                  (game) => game.eventId === data.eventId
                );
                if (!game) {
                  return;
                }
                game.away = {
                  ...game.away,
                  ...data.away,
                };

                game.home = {
                  ...game.home,
                  ...data.home,
                };

                game.clock = {
                  ...game.clock,
                  ...data.clock,
                };

                game.period = {
                  ...game.period,
                  ...data.period,
                };
              }
            ) as ThunkAction<void, unknown, unknown, Action>
          );
        } else {
          api.dispatch(liveApi.util.invalidateTags(["ScoresFeed"]));
        }
      }

      api.dispatch(
        liveApi.util.updateQueryData(
          "game",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            draft.scoreboard = data;
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );
    }
  ),
  liveGameTeamStatsUpdate: createAsyncThunk(
    "momentumApi/realtime/liveGameTeamStatsUpdate",
    async (message: RealtimeMessage, api) => {
      const { data } = parse(liveGameTeamStatsUpdateSchema, message);

      api.dispatch(
        liveApi.util.updateQueryData(
          "game",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            draft.teamStats = data;
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );
    }
  ),
  livePbpEventUpdate: createAsyncThunk(
    "momentumApi/realtime/livePbpEventUpdate",
    async (message: RealtimeMessage, api) => {
      const { data } = parse(livePbpEventUpdateSchema, message);

      api.dispatch(
        liveApi.util.updateQueryData(
          "gamePbp",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            if (!draft.pbpEvents) {
              draft.pbpEvents = [];
            }

            const index = draft.pbpEvents.findIndex(
              (pbpEvent) => pbpEvent.id === data.pbp.id
            );
            if (index === -1) {
              draft.pbpEvents.push(data.pbp);
              draft.pbpEvents.sort((a, b) => b.sequence - a.sequence);
            } else {
              draft.pbpEvents[index] = data.pbp;
            }
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );

      api.dispatch(
        liveApi.util.updateQueryData(
          "game",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            if (!draft.pbpEvents) {
              draft.pbpEvents = [];
            }

            const index = draft.pbpEvents.findIndex(
              (pbpEvent) => pbpEvent.id === data.pbp.id
            );
            if (index === -1) {
              draft.pbpEvents.push(data.pbp);
              draft.pbpEvents.sort((a, b) => b.sequence - a.sequence);
            } else {
              draft.pbpEvents[index] = data.pbp;
            }
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );
    }
  ),
  livePbpEventDelete: createAsyncThunk(
    "momentumApi/realtime/livePbpEventDelete",
    async (message: RealtimeMessage, api) => {
      const { data } = parse(livePbpEventDeleteSchema, message);
      api.dispatch(
        liveApi.util.updateQueryData(
          "gamePbp",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            if (draft.pbpEvents) {
              draft.pbpEvents = draft.pbpEvents.filter(
                (pbpEvent) => pbpEvent.id !== data.pbpEventId
              );
            }
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );

      api.dispatch(
        liveApi.util.updateQueryData(
          "game",
          { eventId: data.eventId, leagueId: data.leagueId },
          (draft) => {
            if (draft.pbpEvents) {
              draft.pbpEvents = draft.pbpEvents.filter(
                (pbpEvent) => pbpEvent.id !== data.pbpEventId
              );
            }
          }
        ) as ThunkAction<void, unknown, unknown, Action>
      );
    }
  ),
} satisfies Record<string, AsyncThunk<void, RealtimeMessage, object>>;

const liveApiListenerMiddleware = createListenerMiddleware({});

liveApiListenerMiddleware.startListening({
  actionCreator: messageReceived,
  async effect(action, listenerApi) {
    if (
      action.payload.message.name &&
      action.payload.message.name in messageHandlers
    ) {
      listenerApi.dispatch(
        messageHandlers[
          action.payload.message.name as keyof typeof messageHandlers
        ](action.payload.message) as ThunkAction<void, unknown, unknown, Action>
      );
    }
  },
});

liveApiListenerMiddleware.startListening({
  predicate: (action) =>
    action.type.startsWith("momentumApi/realtime/") && isRejected(action),
  effect: (action) => {
    if (isRejected(action)) {
      logger.error(action.error.message);
      console.error(action.error);
    }
  },
});

dynamicMiddleware.addMiddleware(liveApiListenerMiddleware.middleware);
